import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { BookOpen, CheckCircle, Clock, TrendingUp, CalendarDays } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({ total_courses: 0, completed_courses: 0, progress_percentage: 0 });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/api/progress/stats`, { headers }),
        axios.get(`${API_URL}/api/courses`, { headers })
      ]);
      setStats(statsRes.data);
      setCourses(coursesRes.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = [
    { name: 'Completed', value: stats.completed_courses, color: 'hsl(173, 58%, 39%)' },
    { name: 'Remaining', value: stats.total_courses - stats.completed_courses, color: 'hsl(210, 20%, 96%)' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen pt-14 lg:pt-0">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background">
          <div className="animate-pulse">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen pt-14 lg:pt-0" data-testid="student-dashboard">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background overflow-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-primary mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">
            {user?.program === 'doctor' ? 'Doctor of Humanitarian Action & Development' : user?.program === 'mdppm' ? 'Master of Development Project & Program Management' : 'Master of Humanitarian Action & Development'}
          </p>
        </div>

        {/* Visitor Banner */}
        {user?.role === 'visitor' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="visitor-banner">
            <p className="text-amber-800 text-sm font-medium">You are viewing this LMS as a visitor. Access to lessons and submissions is restricted.</p>
          </div>
        )}

        {/* Enrollment Period Card */}
        {(user?.activation_start || user?.activation_end) && (
          <Card className="border border-border shadow-none mb-6 animate-fade-in" data-testid="enrollment-period-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary">Enrollment Period</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(user?.activation_start) || 'Not set'} — {formatDate(user?.activation_end) || 'Not set'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border border-border shadow-none hover:shadow-md transition-shadow animate-fade-in animate-delay-100" data-testid="enrolled-courses-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wide">Enrolled Courses</p>
                  <p className="text-3xl font-light text-primary mt-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {stats.total_courses}
                  </p>
                </div>
                <div className="w-12 h-12 bg-secondary rounded-sm flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border shadow-none hover:shadow-md transition-shadow animate-fade-in animate-delay-200" data-testid="completed-courses-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wide">Completed</p>
                  <p className="text-3xl font-light text-primary mt-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {stats.completed_courses}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-sm flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border shadow-none hover:shadow-md transition-shadow animate-fade-in animate-delay-300" data-testid="progress-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wide">Progress</p>
                  <p className="text-3xl font-light text-primary mt-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {stats.progress_percentage}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-sm flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Overview & Recent Courses */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Progress Chart */}
          <Card className="border border-border shadow-none animate-fade-in animate-delay-400" data-testid="progress-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Course Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'hsl(173, 58%, 39%)' }} />
                  <span className="text-sm text-muted-foreground">Completed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-muted" />
                  <span className="text-sm text-muted-foreground">Remaining</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Courses */}
          <Card className="lg:col-span-2 border border-border shadow-none animate-fade-in animate-delay-400" data-testid="recent-courses">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Your Courses
                </CardTitle>
                <Link to="/courses" className="text-sm text-primary hover:underline" data-testid="view-all-courses">
                  View All
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {courses.map((course, index) => (
                  <Link
                    key={course.id}
                    to={`/courses/${course.id}`}
                    className="group"
                    data-testid={`course-card-${index}`}
                  >
                    <div className="border border-border rounded-sm overflow-hidden hover:shadow-md transition-all duration-200">
                      <div className="h-24 bg-muted overflow-hidden">
                        {course.image_url ? (
                          <img
                            src={course.image_url}
                            alt={course.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h3 className="font-medium text-sm text-primary line-clamp-1 group-hover:text-primary/80">
                          {course.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {course.is_capstone ? 'Capstone Project' : `Course ${course.order}`}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Overall Progress Bar */}
        <Card className="mt-6 border border-border shadow-none animate-fade-in" data-testid="overall-progress">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Overall Program Progress</span>
              <span className="text-sm text-muted-foreground">{stats.completed_courses} of {stats.total_courses} courses</span>
            </div>
            <Progress value={stats.progress_percentage} className="h-2" />
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
